import {
  BankAccountInfo,
  BankAccountRef,
} from '@app/queryTyping';

import { ExtendedAccount } from '@app/common/components/controls/AccountsDropDown/types';

interface RequriedFields {
  readonly id: string
}

export const extendAccount = <T>(
  bankAccount: BankAccountInfo,
  data: T,
): ExtendedAccount<BankAccountInfo & T> => {
  const merged = {
    ...bankAccount,
    ...data,
  };

  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __typename,
    ...extendedAccount
  } = merged;

  return extendedAccount;
};

export const extendAccounts = <T extends RequriedFields>(
  bankAccounts: BankAccountInfo[],
  data: T[],
): ExtendedAccount<BankAccountInfo & T>[] => (
    bankAccounts.reduce(
      (accumulated, current) => {
        const match = data.find((account) => account.id === current.id);

        return (
          match
            ? [...accumulated, extendAccount(current, match)]
            : accumulated
        );
      },
      [] as ExtendedAccount<BankAccountInfo & T>[],
    )
  );

export const findAccount = <T extends RequriedFields>(accounts: T[], id?: string): T | null => (
  (id && accounts.find((account) => account.id === id)) || null
);

export const filterAccounts = <T extends RequriedFields>(
  accounts: T[],
  accountRefs: BankAccountRef[],
): Array<T> => (
    accountRefs.reduce<Array<T>>((result, ref) => {
      const accountByRef = accounts.find((account) => account?.id === ref?.accountId);

      if (accountByRef) {
        result.push(accountByRef);
      }

      return result;
    }, [])
  );

export const filterAccountsByIds = <T extends RequriedFields>(
  accounts: T[],
  accountIds: string[],
): Array<T> => (
    accountIds.reduce<Array<T>>((result, ref) => {
      const accountById = accounts.find((account) => account?.id === ref);

      if (accountById) {
        result.push(accountById);
      }

      return result;
    }, [])
  );
